import {
  type FetchOrderPricingResponse,
  type LineItemType,
  type OrderPricingParams,
  PricingEndpoint,
} from '@alto/scriptdash/alto/pricing/patients/v3/pricing_endpoint';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { apiEndpointHandler } from '~shared/helpers/api';
import { roundToTwo } from '~shared/helpers/currency';
import { itemPricingMap } from '~shared/reducers/pricing';

const PricingApi = PricingEndpoint(apiEndpointHandler);

const IGNORED_LINE_ITEMS_FOR_SUBTOTAL = new Set<LineItemType>([
  'tip_amount',
  'on_demand_fee',
  'delivery_fee',
  'same_day_delivery_fee',
  'next_day_delivery_fee',
  'standard_delivery_fee',
  'asap_delivery_fee',
  'next_day_mail_fee',
]);

const getTotal = (data: FetchOrderPricingResponse) => {
  return data.line_items?.find(({ key }) => key === 'total');
};

/**
 * Get sub-total for order not accounting for tip and other fees (i.e delivery).
 * Used as the summed amount for split payments (tip and fees charged separately)
 */
const getItemsSubtotal = (data: FetchOrderPricingResponse) => {
  const orderTotal = getTotal(data);
  if (!orderTotal) {
    return null;
  }

  const { line_items } = data;
  if (orderTotal.value === 0) {
    return 0;
  }

  if (!orderTotal.value) {
    return null;
  }

  let total = orderTotal.value;
  for (const lineItem of line_items) {
    const { key, value } = lineItem;

    if (IGNORED_LINE_ITEMS_FOR_SUBTOTAL.has(key)) {
      total -= value ?? 0;
    }
  }
  return roundToTwo(total);
};

const isPriceUnconfirmed = (data: FetchOrderPricingResponse) => {
  const orderTotal = getTotal(data);
  if (!orderTotal) {
    return true;
  }

  return orderTotal.info_key === 'price_unavailable' || orderTotal.info_key === 'estimated';
};

export const pricing = createQueryKeys('pricing', {
  order: ({
    shipmentID,
    order,
    bundlingOptionShipmentID,
  }: {
    shipmentID?: number;
    order?: OrderPricingParams;
    bundlingOptionShipmentID?: number;
  }) => ({
    queryKey: [{ shipmentID, order, bundlingOptionShipmentID }],
    queryFn: async () => {
      const response = await PricingApi.fetchOrderPricing(
        { order, shipment_id: shipmentID, bundling_option_shipment_id: bundlingOptionShipmentID },
        { version: 'v6' },
      );

      if (response.errors?.length) {
        throw new Error(response.errors[0].title || 'Error fetching shipment pricing');
      }

      return {
        has_copay: isPriceUnconfirmed(response) || !!getItemsSubtotal(response), // items subtotal returns total minus all fees
        item_pricing: itemPricingMap(response.item_pricing),
        line_items: response.line_items,
        metadata: response.metadata,
        price_unconfirmed: isPriceUnconfirmed(response),
        subtotal: getItemsSubtotal(response),
        total: getTotal(response),
      };
    },
  }),
  prescriptionById: ({ prescriptionId }: { prescriptionId: number }) => ({
    queryKey: [{ prescriptionId }],
    queryFn: async () => {
      const response = await PricingApi.fetchPrescriptionPricingById({ id: prescriptionId }, { version: 'v4' });

      return {
        item_pricing: response.item_pricing,
        errors: response.errors,
      };
    },
  }),
});
